import { GeographicCoordinates } from '@/api/main/types.gen'
import { minBy } from 'ramda'

/**
 * Normalize an azimuth value to the range [0, 360)
 * Handles negative values and values >= 360
 */
export const normalizeAzimuth = (azimuth: number): number => {
  return ((azimuth % 360) + 360) % 360
}

/**
 * Calculate an azimuth that
 * a) is equal to the desired one and
 * b) doesn't steer more than 180 degrees from the previous one
 */
export const calculateCorrectedAzimuth = (previousAzimuth: number, azimuth: number): number => {
  const simplifiedAzimuth = ((azimuth % 360) + 360) % 360

  const turns = Math.floor(previousAzimuth / 360)

  return normalizeAzimuth(
    [
      simplifiedAzimuth + (turns + 1) * 360,
      simplifiedAzimuth + turns * 360,
      simplifiedAzimuth + (turns - 1) * 360,
    ].reduce(minBy<number>(azimuthCandidate => Math.abs(azimuthCandidate - previousAzimuth)))
  )
}

export const calculateAzimuth = (source: GeographicCoordinates, destination: GeographicCoordinates): number => {
  const toRadians = (deg: number) => (deg * Math.PI) / 180
  const toDegrees = (rad: number) => (rad * 180) / Math.PI

  const source_lat_rad = toRadians(source.latitude)
  const destination_lat_rad = toRadians(destination.latitude)
  const deltaLon = toRadians(destination.longitude - source.longitude)

  const y = Math.sin(deltaLon) * Math.cos(destination_lat_rad)
  const x =
    Math.cos(source_lat_rad) * Math.sin(destination_lat_rad) -
    Math.sin(source_lat_rad) * Math.cos(destination_lat_rad) * Math.cos(deltaLon)

  const heading = toDegrees(Math.atan2(y, x))
  return (heading + 360) % 360
}
